import { create } from 'zustand';

export interface Layer {
  id: string;
  name: string;
  type: '3d-model' | '2d-component' | 'group';
  visible: boolean;
  locked: boolean;
  parentId?: string;
  children?: string[];
  modelIds?: string[];
}

interface LayerState {
  layers: Layer[];
  selectedLayerId: string | null;
  addLayer: (layer: Omit<Layer, 'id'>) => void;
  removeLayer: (id: string) => void;
  updateLayer: (id: string, updates: Partial<Layer>) => void;
  selectLayer: (id: string | null) => void;
  toggleVisibility: (id: string) => void;
  toggleLock: (id: string) => void;
  reorderLayers: (fromIndex: number, toIndex: number) => void;
  addChildToGroup: (groupId: string, childId: string) => void;
  removeChildFromGroup: (groupId: string, childId: string) => void;
  addModelToLayer: (layerId: string, modelId: string) => void;
  removeModelFromLayer: (layerId: string, modelId: string) => void;
  setLayers: (layers: Layer[]) => void;
}

// 纯辅助函数：收集某图层（含递归 children 子图层）下关联的所有 modelIds
export function collectLayerModelIds(layers: Layer[], layerId: string): string[] {
  const layer = layers.find((l) => l.id === layerId);
  if (!layer) return [];
  const ids = new Set<string>(layer.modelIds || []);
  (layer.children || []).forEach((childId) => {
    collectLayerModelIds(layers, childId).forEach((id) => ids.add(id));
  });
  return Array.from(ids);
}

// 纯辅助函数：收集所有"锁定图层/组"（含父组锁定时子层继承）覆盖的模型
export function collectLockedModelIds(layers: Layer[]): string[] {
  const locked = new Set<string>();
  const walk = (layer: Layer, parentLocked: boolean) => {
    const isLocked = parentLocked || layer.locked;
    if (isLocked) {
      (layer.modelIds || []).forEach((id) => locked.add(id));
    }
    (layer.children || []).forEach((childId) => {
      const child = layers.find((l) => l.id === childId);
      if (child) walk(child, isLocked);
    });
  };
  layers.forEach((layer) => walk(layer, false));
  return Array.from(locked);
}

const useLayerStore = create<LayerState>((set) => ({
  layers: [],
  selectedLayerId: null,

  addLayer: (layer) =>
    set((state) => ({
      layers: [
        ...state.layers,
        {
          ...layer,
          modelIds: layer.modelIds ?? [],
          id: `layer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        },
      ],
    })),

  removeLayer: (id) =>
    set((state) => {
      const idsToRemove = new Set<string>([id]);
      const collect = (layerId: string) => {
        const layer = state.layers.find((l) => l.id === layerId);
        layer?.children?.forEach((cid) => {
          if (!idsToRemove.has(cid)) {
            idsToRemove.add(cid);
            collect(cid);
          }
        });
      };
      collect(id);
      return {
        layers: state.layers
          .filter((l) => !idsToRemove.has(l.id))
          .map((l) => ({
            ...l,
            children: (l.children || []).filter((c) => !idsToRemove.has(c)),
          })),
        selectedLayerId: idsToRemove.has(state.selectedLayerId || '') ? null : state.selectedLayerId,
      };
    }),

  updateLayer: (id, updates) =>
    set((state) => ({
      layers: state.layers.map((l) => (l.id === id ? { ...l, ...updates } : l)),
    })),

  selectLayer: (id) => set({ selectedLayerId: id }),

  toggleVisibility: (id) =>
    set((state) => ({
      layers: state.layers.map((l) =>
        l.id === id ? { ...l, visible: !l.visible } : l
      ),
    })),

  toggleLock: (id) =>
    set((state) => ({
      layers: state.layers.map((l) =>
        l.id === id ? { ...l, locked: !l.locked } : l
      ),
    })),

  reorderLayers: (fromIndex, toIndex) =>
    set((state) => {
      const newLayers = [...state.layers];
      const [removed] = newLayers.splice(fromIndex, 1);
      newLayers.splice(toIndex, 0, removed);
      return { layers: newLayers };
    }),

  addChildToGroup: (groupId, childId) =>
    set((state) => ({
      layers: state.layers.map((l) =>
        l.id === groupId
          ? { ...l, children: [...(l.children || []), childId] }
          : l
      ),
    })),

  removeChildFromGroup: (groupId, childId) =>
    set((state) => ({
      layers: state.layers.map((l) =>
        l.id === groupId
          ? { ...l, children: (l.children || []).filter((c) => c !== childId) }
          : l
      ),
    })),

  addModelToLayer: (layerId, modelId) =>
    set((state) => ({
      layers: state.layers.map((l) =>
        l.id === layerId && !(l.modelIds || []).includes(modelId)
          ? { ...l, modelIds: [...(l.modelIds || []), modelId] }
          : l
      ),
    })),

  removeModelFromLayer: (layerId, modelId) =>
    set((state) => ({
      layers: state.layers.map((l) =>
        l.id === layerId
          ? { ...l, modelIds: (l.modelIds || []).filter((m) => m !== modelId) }
          : l
      ),
    })),

  setLayers: (layers) => set({ layers, selectedLayerId: null }),
}));

export default useLayerStore;
